import { useState } from "react";
import { Link } from "react-router";
import {
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useTranslate,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fullName, useTeam } from "@/tenax/tasks/taskData";
import { useDay } from "./racketHooks";
import { LendDialog, type LendTarget } from "./LendDialog";
import {
  LOAN_OUTCOMES,
  RACKET_RESOURCES,
  useLoanPurposes,
  type Loan,
  type LoanOutcome,
  type Movement,
} from "./racketData";

export const HolderName = ({
  contactId,
  companyId,
  link = true,
}: {
  contactId: number | null;
  companyId: number | null;
  link?: boolean;
}) => {
  const { data: contact } = useGetOne(
    "contacts",
    { id: contactId! },
    { enabled: contactId != null },
  );
  const { data: company } = useGetOne(
    "companies",
    { id: companyId! },
    { enabled: contactId == null && companyId != null },
  );
  if (contactId == null && companyId == null) return <>—</>;
  const name = contact
    ? [contact.first_name, contact.last_name].filter(Boolean).join(" ")
    : (company?.name ?? "…");
  const to =
    contactId != null
      ? `/contacts/${contactId}/show`
      : `/companies/${companyId}/show`;
  return link ? (
    <Link to={to} className="underline">
      {name}
    </Link>
  ) : (
    <>{name}</>
  );
};

// The outcome of a trial in one tap: converted, returned, lost. Recording
// it closes the loan's automatic tasks (database trigger).
export const OutcomeButtons = ({ loan }: { loan: Loan }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const setOutcome = async (outcome: LoanOutcome) => {
    try {
      await dataProvider.update("test_racket_loans", {
        id: loan.id,
        data: { outcome },
        previousData: loan,
      });
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
      return;
    }
    await Promise.all(
      [...RACKET_RESOURCES, "tasks", "today_items"].map((resource) =>
        queryClient.invalidateQueries({ queryKey: [resource] }),
      ),
    );
    notify("tenax.rackets.outcome.saved", { type: "info" });
  };
  return (
    <div
      role="group"
      aria-label={translate("tenax.rackets.fields.outcome")}
      className="flex flex-wrap gap-1"
    >
      {LOAN_OUTCOMES.map((outcome) => (
        <Button
          key={outcome}
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setOutcome(outcome)}
        >
          {translate(`tenax.rackets.outcome.${outcome}`)}
        </Button>
      ))}
    </div>
  );
};

export const LoanRow = ({ loan }: { loan: Loan }) => {
  const translate = useTranslate();
  const day = useDay();
  const { data: purposes = [] } = useLoanPurposes();
  const { data: team = [] } = useTeam();
  const { data: movements = [] } = useGetList<Movement & { code?: string }>(
    "test_racket_movements",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "id", order: "ASC" },
      filter: { loan_id: loan.id },
    },
  );
  const { data: rackets = [] } = useGetList<{ id: number; code: string }>(
    "test_rackets",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "code", order: "ASC" },
      filter: {
        "id@in": `(${[...new Set(movements.map((m) => m.racket_id))].join(",") || 0})`,
      },
    },
    { enabled: movements.length > 0 },
  );
  const purpose = purposes.find((item) => item.id === loan.purpose_id);
  const lentBy = team.find((member) => member.id === loan.lent_by);
  return (
    <li
      aria-label={translate("tenax.rackets.loan_of", {
        day: day(loan.lent_at),
      })}
      className="flex flex-col gap-1 rounded-md border px-3 py-2 text-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{purpose?.name}</span>
        <span className="text-muted-foreground">
          {translate("tenax.rackets.lent_on", { day: day(loan.lent_at) })}
          {lentBy ? ` · ${fullName(lentBy)}` : ""}
        </span>
        {loan.closed_at ? (
          <Badge variant="secondary">
            {translate("tenax.rackets.closed_on", { day: day(loan.closed_at) })}
          </Badge>
        ) : null}
        {loan.outcome ? (
          <Badge>{translate(`tenax.rackets.outcome.${loan.outcome}`)}</Badge>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2">
        {rackets.map((racket) => (
          <Link
            key={racket.id}
            to={`/test_rackets/${racket.id}/show`}
            className="font-mono underline"
          >
            {racket.code}
          </Link>
        ))}
      </div>
      {purpose?.tracks_outcome && !loan.outcome ? (
        <OutcomeButtons loan={loan} />
      ) : null}
    </li>
  );
};

// Loans of a person, an organization or a deal, with "Lend rackets" (task
// 1.10b, D-040)
export const RelatedLoans = ({ target }: { target: LendTarget }) => {
  const translate = useTranslate();
  const [lending, setLending] = useState(false);
  const filter =
    target.dealId != null
      ? { deal_id: target.dealId }
      : target.contactId != null
        ? { contact_id: target.contactId }
        : { company_id: target.companyId };
  const { data: loans = [] } = useGetList<Loan>("test_racket_loans", {
    pagination: { page: 1, perPage: 20 },
    sort: { field: "lent_at", order: "DESC" },
    filter,
  });
  return (
    <section
      aria-label={translate("tenax.rackets.loans")}
      className="flex flex-col gap-2"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          {translate("tenax.rackets.loans")}
        </h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setLending(true)}
        >
          {translate("tenax.rackets.lend.button")}
        </Button>
      </div>
      {loans.length ? (
        <ul className="flex flex-col gap-2">
          {loans.map((loan) => (
            <LoanRow key={loan.id} loan={loan} />
          ))}
        </ul>
      ) : null}
      {lending ? (
        <LendDialog target={target} onClose={() => setLending(false)} />
      ) : null}
    </section>
  );
};
